import { useCallback, useMemo, useState } from 'react';
import type { ColumnDef, Row, Table } from '@tanstack/react-table';
import { Checkbox } from '@/components/ui/checkbox';
import { CellAction } from './cell-action';
import type { Organisation } from '@/services/types';
import { useAuthStore } from '@/store/auth';

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
});

function SelectHeader({ table, checkedRowIds, onToggleAll }: { table: Table<any>; checkedRowIds: string[]; onToggleAll: (ids: string[]) => void }) {
  const allRowIds = table.getCoreRowModel().rows.map((row) => row.original.id);
  const isAllSelected = allRowIds.length > 0 && allRowIds.every(id => checkedRowIds.includes(id));
  
  return (
    <Checkbox
      checked={isAllSelected}
      onCheckedChange={(value) => {
        if (value) {
          onToggleAll(allRowIds);
        } else {
          onToggleAll([]);
        }
      }}
    />
  );
}

function SelectCell({ row, checked, onChange }: { row: any; checked: boolean; onChange: (id: string, value: boolean) => void }) {
  const id = row.original.id;
  return (
    <Checkbox
      onClick={(e) => e.stopPropagation()}
      checked={checked}
      onCheckedChange={(value) => {
        onChange(id, !!value);
      }}
    />
  );
}

// Hook to build columns with internal state
export function useOrganisationColumns() {
  const [checkedRowIds, setCheckedRowIds] = useState<string[]>([]);
  const { context } = useAuthStore();

  const handleToggleAll = useCallback((ids: string[]) => {
    setCheckedRowIds(ids);
  }, []);

  const handleToggleOne = useCallback((id: string, value: boolean) => {
    setCheckedRowIds((prev) =>
      value ? [...new Set([...prev, id])] : prev.filter((rowId) => rowId !== id)
    );
  }, []);

  const columns: ColumnDef<Organisation>[] = useMemo(() => [
    // only needed for bulk action which is for workspace
    ...(context === 'workspace' ? [
      {
        id: 'select',
        header: ({ table }: { table: Table<Organisation> }) => <SelectHeader table={table} checkedRowIds={checkedRowIds} onToggleAll={handleToggleAll} />,
        cell: ({ row }: { row: Row<Organisation> }) => (
          <SelectCell
            row={row}
            checked={checkedRowIds.includes(row.original.id)}
            onChange={handleToggleOne}
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
    ] : []),
    {
      accessorKey: 'name',
      header: 'Organisation',
      cell: ({ row }) => (
        <div className="max-w-[200px] truncate font-medium">{row.getValue('name')}</div>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email address',
      cell: ({ row }) => (
        <div className="max-w-[200px] truncate">{row.getValue('email')}</div>
      ),
    },
    {
      accessorKey: 'contactPerson.name',
      header: 'Contact person',
      cell: ({ row }) => <div>{row.original.contactPerson?.name || 'N/A'}</div>,
    },
    {
      accessorKey: 'website',
      header: 'Website',
      cell: ({ row }) => {
        const url = row.getValue('website') as string;
        return url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="block max-w-[200px] truncate text-blue-600 hover:underline"
          >
            {url}
          </a>
        ) : (
          <span className="text-gray-500">N/A</span>
        );
      },
    },
    {
      accessorKey: 'phoneNumber',
      header: 'Phone',
      cell: ({ row }) => (
        <div className="max-w-[200px] truncate">{row.getValue('phoneNumber') ?? 'N/A'}</div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Date added',
      cell: ({ row }) => {
        const date = row.getValue('createdAt') as string | Date;
        return date ? dateFormatter.format(new Date(date)) : 'N/A';
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => <CellAction data={row.original} />,
    },
  ], [checkedRowIds, handleToggleAll, handleToggleOne]);

  return { columns, checkedRowIds, setCheckedRowIds };
}